import { zeroAddress } from 'viem'
import { MorphoGeneralPublicResponse } from '../../../types/lender/morpho-types'
import { AdditionalYields, GenericTokenList } from '../../../types'
import { createMarketUid } from '../../../utils'
import { toGenericPriceKey, toOracleKey } from '../../../assets'
import type { CoolerMarketsRaw } from './types'

const WAD = 1e18

const clamp01 = (v: number) => Math.max(0, Math.min(1, v))

/** Wad bigint → human number (MonoCooler amounts are WAD without exception). */
const wadToNumber = (v: bigint | null): number =>
  v !== null ? Number(v) / WAD : 0

function currencyFor(
  address: string,
  decimals: number,
  symbol: string,
  tokens: GenericTokenList,
): any {
  const lower = address.toLowerCase()
  return tokens[lower] ?? { address: lower, symbol, name: symbol, decimals }
}

/**
 * Map the Cooler batch into the shared `MorphoGeneralPublicResponse` shape,
 * keyed by the bare `COOLER` key — ONE market ever (gOHM → the live debt
 * token), so the lender key IS the market key and there is no fan-out.
 *
 *  - COLLATERAL entry (gOHM): deposit-only; `totalDeposits` =
 *    `totalCollateral()` (never `balanceOf` — collateral sits in the DLGTE
 *    module and per-delegate escrows). The protocol's LTV is a PRICE
 *    (debt-per-gOHM wad), so the FRACTIONAL factors here are display-only,
 *    derived against the external gOHM USD price: CF = oltvPrice / collUsd,
 *    LT = lltvPrice / collUsd. Liquidation math must stay price-free — the
 *    raw price-LTVs ride in the `cooler` descriptor as the protocol truth.
 *  - LOAN entry (live `debtToken()`): `totalDebt` = book debt;
 *    `borrowLiquidity` = the treasury's sUSDS headroom (0 when borrows are
 *    paused); the borrow rate is the protocol-set `interestRateWad`
 *    annualised nominally (`rateModel: 'protocolSet'`, no utilization
 *    curve). There is NO supply side — debt is minted from the treasury.
 */
export function convertCoolerMarketsToResponse(
  raw: CoolerMarketsRaw,
  chainId: string,
  prices: { [asset: string]: number } = {},
  _additionalYields: AdditionalYields = {
    intrinsicYields: {},
    lenderRewards: {},
    loaded: true,
  },
  tokens: GenericTokenList = {},
): { [m: string]: MorphoGeneralPublicResponse } {
  const out: { [m: string]: MorphoGeneralPublicResponse } = {}
  const cfg = raw?.config
  if (!cfg || raw.source === 'none') return out

  const lenderKey = raw.lender
  const collAddr = (raw.collateralToken ?? cfg.gohm).toLowerCase()
  // Governance-swappable — the live read wins over the seed's usds.
  const debtAddr = (raw.debtToken ?? cfg.usds).toLowerCase()

  const collToken = tokens[collAddr]
  const debtToken = tokens[debtAddr]
  const collSymbol = collToken?.symbol ?? 'gOHM'
  const debtSymbol = debtToken?.symbol ?? 'USDS'
  const collDecimals = collToken?.decimals ?? 18
  const debtDecimals = debtToken?.decimals ?? cfg.debtDecimals ?? 18

  const collPriceKey =
    toOracleKey(collToken?.assetGroup!) || toGenericPriceKey(collAddr, chainId)
  const collPrice = prices[collPriceKey] ?? 0
  const debtPriceKey =
    toOracleKey(debtToken?.assetGroup!) || toGenericPriceKey(debtAddr, chainId)
  const debtPrice = prices[debtPriceKey] || 1

  // Nominal APR percent — package convention (ln(1.005) wad ⇒ ~0.49875 %).
  const borrowAprPct = wadToNumber(raw.interestRateWad) * 100

  const totalCollateral = wadToNumber(raw.totalCollateral)
  const totalDebt = wadToNumber(raw.totalDebt)
  // The LTVs are PRICES: debt-token units of debt per gOHM token.
  const oltvPrice = wadToNumber(raw.oltvPrice)
  const lltvPrice = wadToNumber(raw.lltvPrice)
  const minDebt = wadToNumber(raw.minDebtRequired)
  const borrowsPaused = raw.borrowsPaused ?? false
  const liquidationsPaused = raw.liquidationsPaused ?? false

  // Display-only fractional factors — the protocol has no ratio LTV, so this
  // is only meaningful against an external gOHM USD price; without one both
  // read 0 rather than inventing a number.
  const borrowCollateralFactor =
    collPrice > 0 ? clamp01(oltvPrice / collPrice) : 0
  const collateralFactor = collPrice > 0 ? clamp01(lltvPrice / collPrice) : 0

  let borrowLiquidity = wadToNumber(raw.susdsHeadroom)
  if (borrowsPaused) borrowLiquidity = 0

  // @ts-ignore — built incrementally, matches the Inverse/River converters
  const entry: MorphoGeneralPublicResponse = { data: {} }

  // 1. COLLATERAL entry — deposit-only.
  const collUid = createMarketUid(chainId, lenderKey, collAddr)
  entry.data[collUid] = {
    marketUid: collUid,
    name: 'Collateral ' + collSymbol,
    poolId: cfg.monoCooler.toLowerCase(),
    underlying: collAddr,
    asset: currencyFor(collAddr, collDecimals, collSymbol, tokens),
    totalDeposits: totalCollateral,
    totalDebtStable: 0,
    totalDebt: 0,
    totalLiquidity: totalCollateral,
    borrowLiquidity: 0,
    totalLiquidityUSD: totalCollateral * collPrice,
    borrowLiquidityUSD: 0,
    totalDepositsUSD: totalCollateral * collPrice,
    totalDebtStableUSD: 0,
    totalDebtUSD: 0,
    utilization: 0,
    depositRate: 0,
    variableBorrowRate: 0,
    stableBorrowRate: 0,
    // gOHM staking emissions are ZERO today — never hardcode a carry here.
    intrinsicYield:
      _additionalYields?.intrinsicYields?.[collToken?.assetGroup!] ?? 0,
    rewards: undefined,
    decimals: collDecimals,
    config: {
      0: {
        category: 0,
        borrowCollateralFactor,
        collateralFactor,
        borrowFactor: 1,
        // Seized collateral is unstaked and BURNED — no liquidator bonus.
        liquidationPenalty: 0,
        closeFactor: 1,
        collateralDisabled: false,
        debtDisabled: true,
      },
    },
    closeFactor: 1,
    collateralActive: true,
    borrowingEnabled: false,
    depositsEnabled: true,
    hasStable: false,
    isActive: true,
    isFrozen: false,
  } as any

  // 2. LOAN entry — the live debt token. No supply side (treasury-minted).
  const loanUid = createMarketUid(chainId, lenderKey, debtAddr)
  entry.data[loanUid] = {
    marketUid: loanUid,
    name: debtSymbol,
    poolId: cfg.monoCooler.toLowerCase(),
    underlying: debtAddr,
    asset: currencyFor(debtAddr, debtDecimals, debtSymbol, tokens),
    totalDeposits: 0,
    totalDebtStable: 0,
    totalDebt,
    totalLiquidity: borrowLiquidity,
    borrowLiquidity,
    totalLiquidityUSD: borrowLiquidity * debtPrice,
    borrowLiquidityUSD: borrowLiquidity * debtPrice,
    totalDepositsUSD: 0,
    totalDebtStableUSD: 0,
    totalDebtUSD: totalDebt * debtPrice,
    utilization: 0,
    depositRate: 0,
    // Protocol-set, continuously compounded on-chain; reported nominally.
    variableBorrowRate: borrowAprPct,
    stableBorrowRate: 0,
    rateModel: 'protocolSet',
    intrinsicYield: 0,
    rewards: undefined,
    decimals: debtDecimals,
    config: {
      0: {
        category: 0,
        borrowCollateralFactor: 0,
        collateralFactor: 0,
        borrowFactor: 1,
        liquidationPenalty: 0,
        closeFactor: 1,
        collateralDisabled: true,
        debtDisabled: borrowsPaused,
      },
    },
    closeFactor: 1,
    collateralActive: false,
    borrowingEnabled: !borrowsPaused,
    depositsEnabled: false,
    hasStable: false,
    variableBorrowDisabled: borrowsPaused,
    isActive: true,
    isFrozen: false,
  } as any

  entry.params = {
    market: {
      lender: lenderKey,
      name: `${debtSymbol} / ${collSymbol}`,
      loanDecimals: debtDecimals,
      collateralDecimals: collDecimals,
      // The MonoCooler contract doubles as the market id.
      id: cfg.monoCooler.toLowerCase(),
      lltv: String(collateralFactor),
      oracle: cfg.ltvOracle,
      irm: zeroAddress,
      collateralAddress: collAddr,
      loanAddress: debtAddr,
      // --- Cooler descriptor (live snapshot; consumed by calldata builders
      // + worker-api resolvers + the term-sheet adapter) ---
      cooler: {
        monoCooler: cfg.monoCooler,
        /** Debt-token units, human string. Gates borrows AND partial repays:
         *  any action leaving 0 < debt < minDebt reverts (full repay fine). */
        minDebt: String(minDebt),
        /** THE PROTOCOL TRUTH: LTVs are PRICES (debt per gOHM token), not
         *  ratios — the fractional factors above are display-only. */
        oltvPrice: String(oltvPrice),
        lltvPrice: String(lltvPrice),
        /** Per-year continuously-compounded rate, raw wad string. */
        interestRateWad:
          raw.interestRateWad !== null
            ? raw.interestRateWad.toString()
            : undefined,
        /** The oracle's OLTV drip schedule — future OLTV in closed form. */
        drip: raw.drip
          ? {
              startingValue: raw.drip.startingValue.toString(),
              startTime: raw.drip.startTime.toString(),
              targetValue: raw.drip.targetValue.toString(),
              targetTime: raw.drip.targetTime.toString(),
              slope: raw.drip.slope.toString(),
            }
          : undefined,
        /** LLTV = OLTV × (1 + premiumBps / 1e4); raisable to at most 333. */
        liquidationLtvPremiumBps: raw.liquidationLtvPremiumBps ?? undefined,
        borrowsPaused,
        liquidationsPaused,
        addresses: {
          monoCooler: cfg.monoCooler,
          ltvOracle: cfg.ltvOracle,
          treasuryBorrower: cfg.treasuryBorrower,
          treasury: cfg.treasury,
          susds: cfg.susds,
          gohm: cfg.gohm,
          /** The LIVE debt token — governance-swappable, never pin USDS. */
          debtToken: debtAddr,
        },
      },
    } as any,
  }
  entry.chainId = chainId

  out[lenderKey] = entry
  return out
}
